import InfiniteScroll from 'react-infinite-scroll-component';
import dayjs, { Dayjs } from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { useCallback, useContext, useMemo } from 'react';
import { useEffect, useState } from 'react';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
// import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import ReportTemplatesSelectPaginate from '@/modules/admin-dashboard/components/ReportTemplatesSelectPaginate.tsx';
import BatchReportItem from '@/modules/admin-dashboard/components/items/BatchReportItem.tsx';
import BatchReportsModal from '@/modules/admin-dashboard/components/modals/BatchReportsModal.tsx';
import GenerateBatchReportsModal from '@/modules/admin-dashboard/components/modals/GenerateBatchReportsModal.tsx';
import { useGetReportsBatchListQuery } from '@/modules/admin-dashboard/queries/reports-batch.ts';
import {
  Button,
  EmptyListState,
  FilterItem,
  ModalProviderContext,
  PageLayout,
  SearchField,
  SelectPaginateOption,
  Spinner,
} from 'ui';

interface Filters {
  archived?: boolean;
  date_from?: string;
  date_to?: string;
  filter_by_name?: string;
  template?: SelectPaginateOption<number>;
}

const BatchReports = () => {
  const businessId = useGetCurrentBusinessId();
  const [filters, setFilters] = useState<Filters>({});

  useEffect(() => {
    setFilters({});
  }, [businessId]);
  const { openModal } = useContext(ModalProviderContext);

  const { data, isLoading, hasNextPage, fetchNextPage } = useGetReportsBatchListQuery({
    page: 1,
    page_size: 18,
    ...filters,
    filter_by_template_id: filters?.template?.value,
  });

  const batches = useMemo(() => (data?.pages || []).flatMap(({ data }) => data), [data?.pages]);

  const handleOpenBatchReportsModal = useCallback(() => {
    openModal(BatchReportsModal, {
      onSuccess: (id: number, name: string) => {
        openModal(GenerateBatchReportsModal, { id, batchName: name });
      },
    });
  }, [openModal]);

  const handleChangeDatesFilter = useCallback(
    (value: [Dayjs, Dayjs] | null) => {
      setFilters(filters =>
        value
          ? {
              ...filters,
              date_from: value[0].toISOString(),
              date_to: value[1].endOf('day').toISOString(),
            }
          : omit(filters, ['date_from', 'date_to']),
      );
    },
    [setFilters],
  );

  const handleChangeTemplateFilter = useCallback(
    (template: SelectPaginateOption<number> | null) => {
      setFilters(filters => (template ? { ...filters, template } : omit(filters, ['template'])));
    },
    [setFilters],
  );

  const handleSetSearchFilter = useCallback(
    (filter_by_name: string) => {
      setFilters(filters =>
        filter_by_name ? { ...filters, filter_by_name } : omit(filters, ['filter_by_name']),
      );
    },
    [setFilters],
  );

  // const handleToggleArchiveFilter = useCallback((active: boolean) => {
  //   setFilters(filters => (active ? { ...filters, archived: true } : omit(filters, ['archived'])));
  // }, []);

  return (
    <PageLayout title="Batch reports">
      <div className="flex flex-wrap gap-2 justify-between">
        <div className="flex gap-2.5">
          <FilterItem
            type="date"
            placeholder="Date"
            onChange={handleChangeDatesFilter}
            value={
              filters.date_from && filters.date_to
                ? [dayjs(filters.date_from), dayjs(filters.date_to)]
                : undefined
            }
          />
          <FilterItem
            type="custom"
            classes={{
              element: 'min-w-[175px] [&>div>span]:w-full [&_*]:!border-none w-full !py-0 !pr-0.5',
            }}
          >
            <ReportTemplatesSelectPaginate
              isClearable
              value={filters.template || null}
              isMulti={false}
              placeholder="Template"
              className='[&_div[class*="control"]]:p-0'
              onChange={handleChangeTemplateFilter}
            />
          </FilterItem>
        </div>
        <div className="flex gap-2.5 flex-wrap">
          {/*<ArchiveSwitch active={!!filters.archived} onChange={handleToggleArchiveFilter} />*/}
          <SearchField
            placeholder="Search"
            value={filters.filter_by_name || ''}
            onChange={handleSetSearchFilter}
            className="max-w-[200px]"
          />
          <Button onClick={handleOpenBatchReportsModal} className="w-fit">
            Create new batch
          </Button>
        </div>
      </div>
      {isLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      <EmptyListState
        title="No batches yet"
        description="Batch reports help combine multiple templates for quick and easy generation of frequently used reports"
        isLoading={isLoading}
        itemsCount={batches.length}
        isSearchUsed={!isEmpty(filters)}
      />
      {!isLoading && !!batches.length && (
        <InfiniteScroll
          next={fetchNextPage}
          hasMore={!!hasNextPage}
          loader={
            <div className="flex justify-center items-center h-16 w-full">
              <Spinner size="md" variant="primary" />
            </div>
          }
          dataLength={batches.length}
          className="mt-2.5 space-y-2"
          scrollableTarget="scrollable"
        >
          {batches.map(batch => (
            <BatchReportItem key={batch.id} batch={batch} />
          ))}
        </InfiniteScroll>
      )}
    </PageLayout>
  );
};

export default BatchReports;
